import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Employments } from 'server/shared/collections/employments';
import { getPoolerEmploymentsQuery } from 'server/shared/helpers/employments-query/getPoolerEmploymentsQuery';
import { Employment } from 'server/shared/models/employment';
import { PoolerEmploymentTabIdEnum } from 'server/shared/models/PoolerEmploymentTabIdEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { PoolerUser } from 'server/shared/models/user';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { ToastService } from '../../services/toast/toast.service';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../shared/subscription.service';

@Component({
  selector: 'app-pooler-employments-index',
  templateUrl: './pooler-employments-index.component.html',
  styleUrls: ['./pooler-employments-index.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolerEmploymentsIndexComponent implements OnInit, OnDestroy {
  currentUser$ = this.userService.readyPooler$;
  private destroyed$ = new Subject<void>();

  tabParam$: Observable<PoolerEmploymentTabIdEnum> =
    this.route.queryParams.pipe(
      map((params) => params.tab ?? PoolerEmploymentTabIdEnum.NEW),
      // distinctUntilChanged is needed in case of other params changing
      distinctUntilChanged(),
    );

  searchParam$: Observable<string | undefined> = this.route.queryParams.pipe(
    map((params) => params.searchText),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );

  rangeParam$: Observable<string | undefined> = this.route.queryParams.pipe(
    map((params) => params.range ?? 'ALL'),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );

  employmentsQuery$ = combineLatest([
    this.tabParam$,
    this.searchParam$,
    this.rangeParam$,
    this.currentUser$,
  ]).pipe(
    map(([tab, search, range, currentUser]) => {
      return getPoolerEmploymentsQuery(tab, search, range, currentUser);
    }),
  );

  employments$ = this.employmentsQuery$.pipe(
    switchMap((query) => {
      return Employments.find(query).pipe(debounceTime(10));
    }),
    // Makes sure the pipe is shared between different subscribers / async pipes
    shareReplay(1),
    rxNgZoneRun(this.ngZone),
  );

  readonly tabs: {
    label: string;
    value: PoolerEmploymentTabIdEnum;
    svgIcon: string;
  }[] = [
    {
      label: 'new',
      value: PoolerEmploymentTabIdEnum.NEW,
      svgIcon: 'inbox',
    },
    {
      label: 'answered',
      value: PoolerEmploymentTabIdEnum.ANSWERED,
      svgIcon: 'e-mail-read-check',
    },
    {
      label: 'booked',
      value: PoolerEmploymentTabIdEnum.BOOKED,
      svgIcon: 'board-booked-star',
    },
    {
      label: 'archived',
      value: PoolerEmploymentTabIdEnum.ARCHIVED,
      svgIcon: 'archive-box',
    },
  ];

  readonly dateRangeChips = ['ALL', 'TODAY', 'MONTH', 'NEXT_MONTH', 'CUSTOM'];

  constructor(
    private ngZone: NgZone,
    private toastService: ToastService,
    private subscriptionService: SubscriptionService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.userService.readyUser$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        if (user.role !== RoleEnum.POOL) {
          this.router.navigate(['/']);
        }
      });
  }

  ngOnInit(): void {
    combineLatest([this.tabParam$, this.currentUser$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([tab, user]) => {
        if (user) {
          this.applySavedDefaultFilter(tab, user);
        }
      });
    this.subscriptionService.poolerEmployments.subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  switchTab(tabId: PoolerEmploymentTabIdEnum) {
    this.mergeQueryParams({
      tab: tabId,
    });
  }

  mergeQueryParams(queryParams: Params) {
    this.router
      .navigate([], {
        queryParamsHandling: 'merge',
        queryParams,
      })
      .then();
  }

  applySavedDefaultFilter(tab: PoolerEmploymentTabIdEnum, user: PoolerUser) {
    const resetParamsObject = {
      searchText: null,
      range: null,
    };
    if (
      !user ||
      !user.employmentDefaultFilters ||
      !user.employmentDefaultFilters[tab]
    ) {
      this.mergeQueryParams(resetParamsObject);
      return;
    }
    this.mergeQueryParams({
      ...resetParamsObject,
      ...user.employmentDefaultFilters[tab],
    });
  }

  saveDefaultFilter() {
    combineLatest([this.tabParam$, this.currentUser$])
      .pipe(take(1))
      .subscribe(([tab, user]) => {
        if (!user) return;
        const resetParamsObject = {
          searchText: null,
          range: null,
        };
        Meteor.call('savePoolerEmploymentDefaultFilter', tab, {
          ...resetParamsObject,
          ...this.route.snapshot.queryParams,
        });
        this.toastService.add({
          type: 'SUCCESS',
          message: 'savedDefaultFilter',
          timeout: 5000,
        });
      });
  }

  trackById(index: number, object: Employment): string {
    return object._id;
  }
}
